import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getErrorStatus, getErrorMessage } from '@common/utils';
import { RouterProvider } from 'react-router-dom';
import { useEffect } from 'react';
import { StatusCodes } from 'http-status-codes';
import { EnvironmentTypes } from '@common/constants';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { AbilityProvider } from './context/AbilityContext';
import RouterWrapper from './router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const errorStatus = getErrorStatus(error);
        if (errorStatus >= 400 && errorStatus < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || EnvironmentTypes.PRODUCTION,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ['*'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  // Better error grouping for similar issues
  attachStacktrace: true,

  // Allow for custom fingerprinting rules
  beforeSend(event, hint) {
    // Add release information when available in env
    if (import.meta.env.VITE_APP_VERSION) {
      event.release = import.meta.env.VITE_APP_VERSION;
    }

    const error = hint?.originalException as any;
    const status = getErrorStatus(error);
    const message = getErrorMessage(error);

    // Filter out common non-critical HTTP errors
    const filteredStatuses = [
      StatusCodes.BAD_REQUEST,
      StatusCodes.UNAUTHORIZED,
      StatusCodes.FORBIDDEN,
      StatusCodes.NOT_FOUND,
    ];

    if (filteredStatuses.includes(status)) {
      return null;
    }

    // TODO: Remove this filter after fixing the root cause
    // Related ticket should address:
    // 1. Uncompressed Asset errors - likely build/CDN compression configuration
    // Filter out asset loading errors (temporary fix to reduce Sentry noise)
    const assetErrorPatterns = ['Uncompressed Asset'];

    if (
      assetErrorPatterns.some((pattern) => message.toLowerCase().includes(pattern.toLowerCase()))
    ) {
      return null;
    }

    return event;
  },

  // Custom error tags
  initialScope: {
    tags: {
      app: 'dashboard',
      environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
    },
  },
});

// Initialize PostHog for analytics and session recording
// Only enabled when API key is configured and not in development
if (
  import.meta.env.VITE_PUBLIC_POSTHOG_KEY &&
  import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT
) {
  try {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      person_profiles: 'identified_only',
      // Only enable session recording in production (not staging)
      disable_session_recording: import.meta.env.MODE !== EnvironmentTypes.PRODUCTION,
      loaded: (posthogInstance) => {
        // Register super properties that will be sent with all events
        posthogInstance.register({
          app: 'dashboard',
          environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
          version: import.meta.env.VITE_APP_VERSION,
        });
      },
    });
  } catch {
    // Analytics failure should not break the application
  }
}

function Root() {
  useEffect(() => {
    const getClientIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const { ip } = await response.json();
        localStorage.setItem('client_ip', ip);
      } catch (error) {
        console.error('Failed to get client IP:', error);
      }
    };

    getClientIP();
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <AbilityProvider>
          <RouterProvider router={RouterWrapper()} />
          <ReactQueryDevtools initialIsOpen={false} />
        </AbilityProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}

export default Root;
